"use client"
import { useEffect, useState, useRef } from "react"

import { TerraLink } from "../../component/TerraLink/TerraLink"
import { SimpleCarousel } from "../../component/SimpleCarousel/SimpleCarousel"

import {
  darkTextureWrapper,
  colorSettingDetection,
  renderHeaderConditionally,
} from "../../utils/functions"
import FeatureCarouselItem, { FeatureItem } from "./FeatureCarouselItem"
import { useScreenSize } from "../../utils/hooks/useScreenSize"
import FeatureBlockQuote, { IFeatureBlockQuote } from "./FeatureBlockQuote"
import { CommonDescription } from "../../component/CommonDescription/CommonDescription"
import { LayoutCommonButtons } from "../../component/CommonButtons/CommonButtons"
import {
  layoutHeaderClasses,
  layoutHeaderBigClasses,
  layoutSubheaderClasses,
  layoutEyebrowClasses,
} from "@/components/tailwindHelpers"

export interface LayoutFeatureCarousel {
  eyebrow?: string
  header?: string
  headerElement?: "h1" | "h2"
  subheader?: string
  description?: string
  buttons?: TerraLink[]
  featureItems?: FeatureItem[]
  featureBlockQuote?: IFeatureBlockQuote
  variant?: "tabs" | "carousel"
  presetTheme?:
    | "t2-theme-one"
    | "t2-theme-two"
    | "t2-theme-three"
    | "t2-theme-four"
    | "t2-theme-five"
    | "t2-theme-six"
    | "t2-theme-seven"
  isHeaderBig?: boolean
  isWrapped?: boolean
  componentImagePriority?: "high" | "auto" | "low"
  isLCP?: boolean
}

export const LayoutFeatureCarousel = ({
  eyebrow,
  header,
  headerElement = "h2",
  subheader,
  description,
  buttons,
  featureItems = [],
  featureBlockQuote,
  presetTheme,
  isHeaderBig,
  isWrapped,
  componentImagePriority,
  isLCP = false,
}: LayoutFeatureCarousel) => {
  const [activeIndex, setActiveIndex] = useState(0)

  const intervalRef = useRef<number | null>(null)
  const prefersReducedMotion = useRef(false)

  const { width } = useScreenSize()

  useEffect(() => {
    prefersReducedMotion.current = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches
  }, [])

  const startAutoPlay = () => {
    if (prefersReducedMotion.current) return
    intervalRef.current = window.setInterval(() => {
      setActiveIndex(prevIndex => (prevIndex + 1) % featureItems.length)
    }, 6000)
  }

  const stopAutoPlay = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
  }

  const handleSlideChange = (newSlide: number) => {
    setActiveIndex(newSlide)
  }

  useEffect(() => {
    startAutoPlay()

    return () => stopAutoPlay()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [featureItems.length])

  useEffect(() => {
    if (width >= 1280) {
      startAutoPlay()
    } else {
      stopAutoPlay()
    }

    return () => stopAutoPlay()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [width, featureItems.length])

  const handleMouseEnter = (index: number) => {
    stopAutoPlay()
    setActiveIndex(index)
  }

  const handleMouseLeave = () => {
    startAutoPlay()
  }

  const handleFocusIn = () => {
    stopAutoPlay()
  }

  const handleFocusOut = () => {
    startAutoPlay()
  }

  const wrapperClasses = `${isWrapped ? "w-full px-5 lg:px-20 m-auto max-w-[1440px]" : ""}`
  const colorSetting = colorSettingDetection(presetTheme || "")
  const mainClasses = `py-[100px] px-0 relative ${presetTheme || ""} ${colorSetting}`
  const headerClasses = `${isHeaderBig ? layoutHeaderBigClasses : layoutHeaderClasses}`
  const finalHeader = renderHeaderConditionally(
    headerClasses,
    header || "",
    headerElement,
  )

  const componentStyles = darkTextureWrapper(presetTheme || "")

  return (
    <div
      className={mainClasses}
      style={componentStyles}
      data-testid="layout-feature-carousel"
    >
      <div className={wrapperClasses}>
        <div className="t2-layout__content t2-container">
          <div className="pb-[200px] flex flex-wrap -mx-2.5 box-border max-xl:pb-5">
            <div className="basis-full max-w-full px-2.5 md:basis-[58.333%] md:max-w-[58.333%] [&:last-child]:mb-0">
              {eyebrow && <div className={layoutEyebrowClasses}>{eyebrow}</div>}
              {finalHeader}
              {subheader && (
                <div className={layoutSubheaderClasses}>{subheader}</div>
              )}
              {description && (
                <CommonDescription
                  className="w-full t2-layout__description"
                  description={description}
                />
              )}
            </div>
          </div>
          {width >= 1280 && (
            <div
              role="region"
              aria-roledescription="carousel"
              aria-label={header ? `${header} features` : "Feature carousel"}
              className="max-w-full w-full flex gap-[15px]"
              style={{
                justifyContent:
                  featureItems?.length < 5 ? "center" : "flex-start",
              }}
              onFocus={handleFocusIn}
              onBlur={handleFocusOut}
            >
              {featureItems &&
                featureItems?.map((item, index) => (
                  <FeatureCarouselItem
                    key={index}
                    index={index}
                    item={item}
                    isActive={index === activeIndex}
                    activeIndex={activeIndex}
                    onMouseEnter={() => handleMouseEnter(index)}
                    onMouseLeave={handleMouseLeave}
                    componentImagePriority={componentImagePriority}
                    isLCP={isLCP}
                    isDesktop
                  />
                ))}
              <div className="sr-only" aria-live="polite" aria-atomic="true">
                {`Showing feature ${activeIndex + 1} of ${featureItems.length}`}
              </div>
            </div>
          )}
        </div>
      </div>
      {width >= 768 && width < 1280 && (
        <div className={buttons && buttons?.length > 0 ? "pb-0" : ""}>
          <SimpleCarousel
            onSlideChange={handleSlideChange}
            slidesPerView={Math.min(3, featureItems.length)}
          >
            {featureItems?.map((item, index) => (
              <FeatureCarouselItem
                key={index}
                index={index}
                item={item}
                isActive={true}
                componentImagePriority={componentImagePriority}
                isLCP={isLCP}
              />
            ))}
          </SimpleCarousel>
        </div>
      )}
      {width < 768 && (
        <div className={buttons && buttons?.length > 0 ? "pb-0" : ""}>
          <SimpleCarousel onSlideChange={handleSlideChange}>
            {featureItems?.map((item, index) => (
              <FeatureCarouselItem
                key={index}
                index={index}
                item={item}
                isActive={true}
                componentImagePriority={componentImagePriority}
                isLCP={isLCP}
              />
            ))}
          </SimpleCarousel>
        </div>
      )}
      {buttons && (
        <div className="flex gap-y-7 md:gap-x-4 md:gap-y-4 max-sm:flex-col max-sm:gap-x-4 justify-center pt-[50px] gap-x-4">
          <LayoutCommonButtons buttons={buttons} />
        </div>
      )}
      {featureBlockQuote && (
        <FeatureBlockQuote
          presetTheme={presetTheme}
          colorSetting={colorSetting}
          componentStyles={componentStyles}
          featureBlockQuote={featureBlockQuote}
        />
      )}
    </div>
  )
}
